<template>
  <div
    class="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden"
    :style="isCollapsed ? 'min-height: auto' : 'min-height: 520px'">
    <!-- Header -->
    <div class="px-4 py-3 bg-gray-50 border-b border-gray-200">
      <div class="flex items-center justify-between">
        <h3 class="text-sm font-semibold text-gray-700 flex items-center">
          <i class="fas fa-chart-bar text-blue-600 mr-2 text-xs" />
          Customer Acquisition Analytics
        </h3>
        <div class="flex items-center space-x-2">
          <span class="text-xs text-gray-400">{{ dateRangeLabel }}</span>
          <button
            @click="isCollapsed = !isCollapsed"
            class="text-gray-400 hover:text-gray-600 transition-colors p-1">
            <i
              :class="isCollapsed ? 'fas fa-chevron-down' : 'fas fa-chevron-up'"
              class="text-xs" />
          </button>
        </div>
      </div>
    </div>

    <!-- Loading State -->
    <div
      v-if="!isCollapsed && isLoading"
      class="p-6 flex items-center justify-center">
      <div role="status">
        <svg
          aria-hidden="true"
          class="w-8 h-8 text-gray-200 animate-spin fill-gray-600"
          viewBox="0 0 100 101"
          fill="none"
          xmlns="http://www.w3.org/2000/svg">
          <!-- eslint-disable max-len -->
          <path
            d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
            fill="currentColor" />
          <path
            d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
            fill="currentFill" />
          <!-- eslint-enable max-len -->
        </svg>
        <span class="sr-only">Loading...</span>
      </div>
    </div>

    <!-- Content -->
    <div
      v-else-if="!isCollapsed"
      class="p-4">
      <!-- Key Metrics Cards -->
      <div class="grid grid-cols-4 gap-3 mb-4">
        <!-- Total New -->
        <div class="bg-gradient-to-r from-blue-500 to-blue-600 rounded-lg p-3 text-white shadow-sm">
          <p class="text-xs text-blue-100 uppercase tracking-wider mb-0.5">
            New Signups
          </p>
          <p class="text-xl font-bold">
            {{ stats.totalNew || 0 }}
          </p>
          <div class="flex items-center mt-1">
            <i
              v-if="stats.growthRate > 0"
              class="fas fa-arrow-up text-emerald-300 text-xs mr-1" />
            <i
              v-else-if="stats.growthRate < 0"
              class="fas fa-arrow-down text-red-300 text-xs mr-1" />
            <span
              class="text-xs"
              :class="{
                'text-emerald-300': stats.growthRate > 0,
                'text-red-300': stats.growthRate < 0,
                'text-blue-200': stats.growthRate === 0
              }">
              {{ Math.abs(stats.growthRate) }}%
            </span>
          </div>
        </div>

        <!-- Average per Day -->
        <div class="bg-blue-50 rounded-lg p-3 border border-blue-200">
          <p class="text-xs text-blue-700 uppercase tracking-wider mb-0.5">
            Avg/Day
          </p>
          <p class="text-xl font-semibold text-gray-900">
            {{ stats.avgPerDay || 0 }}
          </p>
          <p class="text-xs text-blue-600 mt-1">
            Daily average
          </p>
        </div>

        <!-- Peak Day -->
        <div class="bg-emerald-50 rounded-lg p-3 border border-emerald-200">
          <p class="text-xs text-emerald-700 uppercase tracking-wider mb-0.5">
            Peak Day
          </p>
          <p class="text-xl font-semibold text-gray-900">
            {{ stats.peakDay?.count || 0 }}
          </p>
          <p class="text-xs text-emerald-600 mt-1">
            {{ stats.peakDay?.date || 'N/A' }}
          </p>
        </div>

        <!-- Conversion Rate -->
        <div class="bg-gray-50 rounded-lg p-3 border border-gray-200">
          <p class="text-xs text-gray-600 uppercase tracking-wider mb-0.5">
            vs Last Period
          </p>
          <p class="text-xl font-semibold text-gray-900">
            {{ stats.previousTotal || 0 }}
          </p>
          <p class="text-xs text-gray-500 mt-1">
            Previous total
          </p>
        </div>
      </div>

      <!-- Chart and Plan Distribution -->
      <div class="grid grid-cols-3 gap-4">
        <!-- Main Chart - 2 columns -->
        <div class="col-span-2">
          <div class="bg-gray-50 rounded-lg p-3 border border-gray-200">
            <h4 class="text-xs font-semibold text-gray-600 mb-3 uppercase tracking-wider">
              Daily Signups Trend
            </h4>
            <div
              class="chart-container"
              style="position: relative; height: 200px;">
              <canvas ref="chartCanvas" />
            </div>
          </div>
        </div>

        <!-- Plan Distribution - 1 column -->
        <div class="col-span-1">
          <div class="bg-gray-50 rounded-lg p-3 border border-gray-200 h-full">
            <h4 class="text-xs font-semibold text-gray-600 mb-3 uppercase tracking-wider">
              Plan Distribution
            </h4>
            <div class="space-y-2">
              <!-- GPP -->
              <div>
                <div class="flex justify-between items-center mb-1">
                  <span class="text-xs text-gray-600">GPP</span>
                  <span class="text-xs font-medium text-gray-900">
                    {{ stats.planBreakdown?.gpp || 0 }}
                  </span>
                </div>
                <div class="w-full bg-gray-200 rounded-full h-1.5">
                  <div
                    class="bg-emerald-500 h-1.5 rounded-full transition-all duration-500"
                    :style="`width: ${getPlanPercentage('gpp')}%`" />
                </div>
              </div>

              <!-- SiteSense -->
              <div>
                <div class="flex justify-between items-center mb-1">
                  <span class="text-xs text-gray-600">SiteSense</span>
                  <span class="text-xs font-medium text-gray-900">
                    {{ stats.planBreakdown?.sitesense || 0 }}
                  </span>
                </div>
                <div class="w-full bg-gray-200 rounded-full h-1.5">
                  <div
                    class="bg-violet-500 h-1.5 rounded-full transition-all duration-500"
                    :style="`width: ${getPlanPercentage('sitesense')}%`" />
                </div>
              </div>

              <!-- Bundle -->
              <div>
                <div class="flex justify-between items-center mb-1">
                  <span class="text-xs text-gray-600">Bundle</span>
                  <span class="text-xs font-medium text-gray-900">
                    {{ stats.planBreakdown?.bundle || 0 }}
                  </span>
                </div>
                <div class="w-full bg-gray-200 rounded-full h-1.5">
                  <div
                    class="bg-amber-500 h-1.5 rounded-full transition-all duration-500"
                    :style="`width: ${getPlanPercentage('bundle')}%`" />
                </div>
              </div>

              <!-- Other -->
              <div>
                <div class="flex justify-between items-center mb-1">
                  <span class="text-xs text-gray-600">Other</span>
                  <span class="text-xs font-medium text-gray-900">
                    {{ stats.planBreakdown?.other || 0 }}
                  </span>
                </div>
                <div class="w-full bg-gray-200 rounded-full h-1.5">
                  <div
                    class="bg-gray-300 h-1.5 rounded-full transition-all duration-500"
                    :style="`width: ${getPlanPercentage('other')}%`" />
                </div>
              </div>

              <!-- Donut Chart -->
              <div class="pt-3">
                <canvas
                  ref="donutCanvas"
                  style="height: 120px;" />
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Trend Analysis -->
      <div class="mt-4 bg-gray-50 rounded-lg p-3 border border-gray-200">
        <div class="flex items-center justify-between">
          <div class="flex items-center space-x-6">
            <div>
              <span class="text-xs text-gray-600">Weekly Trend</span>
              <div class="flex items-center mt-0.5">
                <Sparkline
                  v-if="weeklyTrend.length > 0"
                  :data="weeklyTrend"
                  :color="'#3F83F8'"
                  :height="20"
                  :width="80" />
              </div>
            </div>
            <div>
              <span class="text-xs text-gray-600">Best Day</span>
              <p class="text-sm font-medium text-gray-900 mt-0.5">
                {{ getBestDayOfWeek() }}
              </p>
            </div>
            <div>
              <span class="text-xs text-gray-600">Momentum</span>
              <p
                class="text-sm font-medium mt-0.5"
                :class="{
                  'text-emerald-600': getMomentumIndicator().includes('↑'),
                  'text-red-600': getMomentumIndicator().includes('↓'),
                  'text-gray-600': getMomentumIndicator().includes('→')
                }">
                {{ getMomentumIndicator() }}
              </p>
            </div>
          </div>
          <div class="text-right">
            <span class="text-xs text-gray-500">Period</span>
            <p class="text-xs font-medium text-gray-700">
              {{ formatDateRange() }}
            </p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import {
  ref, onMounted, watch, computed, nextTick
} from 'vue'
import { Chart, registerables } from 'chart.js'
import axios from '../../init/axios'
import Sparkline from '../components/sparkline.vue'

Chart.register(...registerables)

const props = defineProps({
  startDate: String,
  endDate: String
})

const chartCanvas = ref(null)
const donutCanvas = ref(null)
const chartInstance = ref(null)
const donutInstance = ref(null)
const isLoading = ref(true)
const isCollapsed = ref(false)
const chartData = ref({ labels: [], data: []})
const stats = ref({})
const weeklyTrend = ref([])

const dateRangeLabel = computed(() => {
  const start = props.startDate ? new Date(props.startDate) : new Date()
  const end = props.endDate ? new Date(props.endDate) : new Date()
  const days = Math.round((end - start) / (1000 * 60 * 60 * 24)) + 1
  return `${days} days`
})

const generateWeeklyTrend = () => {
  // Take last 7 days of data for the sparkline
  const last7Days = chartData.value.data.slice(-7)
  weeklyTrend.value = last7Days.length > 0 ? last7Days : [0, 0, 0, 0, 0, 0, 0]
}

const renderMainChart = () => {
  if (!chartCanvas.value) {
    // eslint-disable-next-line no-console
    console.log('Chart canvas not available')
    return
  }

  if (chartInstance.value) {
    chartInstance.value.destroy()
  }

  const ctx = chartCanvas.value.getContext('2d')

  // eslint-disable-next-line no-console
  console.log('Creating main chart with data:', chartData.value)

  chartInstance.value = new Chart(ctx, {
    type: 'line',
    data: {
      labels: chartData.value.labels.map((label) => {
        const date = new Date(label)
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
      }),
      datasets: [{
        label: 'New Signups',
        data: chartData.value.data,
        borderColor: '#3F83F8',
        backgroundColor: 'rgba(63, 131, 248, 0.1)',
        borderWidth: 2,
        tension: 0.3,
        fill: true,
        pointRadius: 0,
        pointHoverRadius: 4,
        pointBackgroundColor: '#3F83F8',
        pointBorderColor: '#fff',
        pointBorderWidth: 2
      }]
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      interaction: {
        intersect: false,
        mode: 'index'
      },
      plugins: {
        legend: {
          display: false
        },
        tooltip: {
          backgroundColor: 'rgba(17, 24, 39, 0.9)',
          titleColor: '#fff',
          bodyColor: '#fff',
          padding: 8,
          displayColors: false,
          callbacks: {
            title: (tooltipItems) => {
              const index = tooltipItems[0].dataIndex
              return chartData.value.labels[index]
            }
          }
        }
      },
      scales: {
        y: {
          beginAtZero: true,
          grid: {
            color: 'rgba(156, 163, 175, 0.1)',
            drawBorder: false
          },
          ticks: {
            color: '#4B5563',
            font: {
              size: 11
            }
          }
        },
        x: {
          grid: {
            display: false
          },
          ticks: {
            color: '#4B5563',
            font: {
              size: 11
            },
            maxRotation: 0,
            autoSkip: true,
            maxTicksLimit: 10
          }
        }
      }
    }
  })
}

const renderDonutChart = () => {
  if (!donutCanvas.value) return

  if (donutInstance.value) {
    donutInstance.value.destroy()
  }

  const breakdown = stats.value.planBreakdown || {}
  const ctx = donutCanvas.value.getContext('2d')

  donutInstance.value = new Chart(ctx, {
    type: 'doughnut',
    data: {
      labels: ['GPP', 'SiteSense', 'Bundle', 'Other'],
      datasets: [{
        data: [
          breakdown.gpp || 0,
          breakdown.sitesense || 0,
          breakdown.bundle || 0,
          breakdown.other || 0
        ],
        backgroundColor: [
          '#0E9F6E',
          '#7E3AF2',
          '#E3A008',
          '#9CA3AF'
        ],
        borderWidth: 0
      }]
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          display: false
        },
        tooltip: {
          backgroundColor: 'rgba(17, 24, 39, 0.9)',
          titleColor: '#fff',
          bodyColor: '#fff',
          padding: 8
        }
      },
      cutout: '70%'
    }
  })
}

const getPlanPercentage = (plan) => {
  const total = stats.value.totalNew || 1
  const count = stats.value.planBreakdown?.[plan] || 0
  return Math.round((count / total) * 100)
}

const getBestDayOfWeek = () => {
  if (!chartData.value.labels.length) return 'N/A'

  const dayCount = {}
  chartData.value.labels.forEach((label, index) => {
    const date = new Date(label)
    const dayName = date.toLocaleDateString('en-US', { weekday: 'long' })
    if (!dayCount[dayName]) dayCount[dayName] = 0
    dayCount[dayName] += chartData.value.data[index] || 0
  })

  const bestDay = Object.entries(dayCount).reduce((a, b) => (a[1] > b[1] ? a : b))
  return bestDay[0]
}

const getMomentumIndicator = () => {
  if (!chartData.value.data.length) return 'Neutral'

  const recentDays = chartData.value.data.slice(-7)
  const olderDays = chartData.value.data.slice(-14, -7)

  if (recentDays.length === 0 || olderDays.length === 0) return 'Neutral'

  const recentAvg = recentDays.reduce((a, b) => a + b, 0) / recentDays.length
  const olderAvg = olderDays.reduce((a, b) => a + b, 0) / olderDays.length

  if (recentAvg > olderAvg * 1.1) return 'Strong ↑'
  if (recentAvg > olderAvg) return 'Positive ↑'
  if (recentAvg < olderAvg * 0.9) return 'Weak ↓'
  if (recentAvg < olderAvg) return 'Negative ↓'
  return 'Neutral →'
}

const formatDateRange = () => {
  const start = props.startDate ? new Date(props.startDate) : new Date()
  const end = props.endDate ? new Date(props.endDate) : new Date()

  const formatOptions = { month: 'short', day: 'numeric' }
  const yearFormat = { year: 'numeric' }

  // Only show year once if both dates are in the same year
  if (start.getFullYear() === end.getFullYear()) {
    // eslint-disable-next-line max-len
    return `${start.toLocaleDateString('en-US', formatOptions)} - ${end.toLocaleDateString('en-US', formatOptions)}, ${end.toLocaleDateString('en-US', yearFormat)}`
  }
  const fullFormatOptions = { month: 'short', day: 'numeric', year: 'numeric' }
  return `${start.toLocaleDateString('en-US', fullFormatOptions)} - ${end.toLocaleDateString('en-US', fullFormatOptions)}`
}

const fetchData = async() => {
  isLoading.value = true

  // eslint-disable-next-line no-console
  console.log('Date props:', { startDate: props.startDate, endDate: props.endDate })

  try {
    const response = await axios.get('/ajax/admin_dashboard/users_chart', {
      params: {
        startDate: props.startDate,
        endDate: props.endDate
      }
    })

    // eslint-disable-next-line no-console
    console.log('Response data:', response.data)

    chartData.value = {
      labels: response.data.labels,
      data: response.data.data
    }
    stats.value = response.data.stats || {}

    // Generate weekly trend from the data
    generateWeeklyTrend()

    // Set loading to false first so DOM updates
    isLoading.value = false

    // Render charts after DOM update
    await nextTick()

    // Render the charts
    renderMainChart()
    renderDonutChart()
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error fetching chart data:', error)
    isLoading.value = false
  }
}

onMounted(fetchData)
watch(() => [props.startDate, props.endDate], fetchData)
</script>

<style scoped>
.chart-container {
  position: relative;
  width: 100%;
}
</style>
